import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ImagePlus, Image as ImageIcon, RotateCcw, Search, Star, X, ExternalLink } from "lucide-react";

export default function AdminHeroTab() {
    const [config, setConfig] = useState({ image_url: "", market: null });
    const [uploading, setUploading] = useState(false);
    const [savingMarket, setSavingMarket] = useState(false);
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const inputRef = useRef(null);

    const load = async () => {
        const { data } = await api.get("/hero/config");
        setConfig(data);
    };
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/hero/config");
                setConfig(data);
            } catch { /* noop */ }
        })();
    }, []);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) { toast.error("Máximo 8 MB"); return; }
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/hero/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Imagen actualizada (${(data.size_bytes / 1024 / 1024).toFixed(1)} MB)`);
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const resetImage = async () => {
        if (!window.confirm("¿Restaurar la imagen original del hero?")) return;
        try {
            await api.post("/admin/hero/reset-image");
            toast.success("Imagen restaurada");
            await load();
        } catch (e) {
            toast.error("Error al restaurar");
        }
    };

    const doSearch = async (q) => {
        if (!q || q.length < 2) { setResults([]); return; }
        setSearching(true);
        try {
            const { data } = await api.get(`/markets`, { params: { q, limit: 20 } });
            setResults(data || []);
        } catch (e) {
            setResults([]);
        } finally { setSearching(false); }
    };

    const setMarket = async (mid) => {
        setSavingMarket(true);
        try {
            await api.patch("/admin/hero/market", { market_id: mid });
            toast.success(mid ? "Mercado destacado actualizado" : "Mercado destacado eliminado");
            setSearch(""); setResults([]);
            await load();
        } catch (e) {
            toast.error("Error");
        } finally { setSavingMarket(false); }
    };

    return (
        <div className="space-y-6" data-testid="admin-hero-tab">
            {/* IMAGE SECTION */}
            <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden">
                <div className="px-5 py-3.5 border-b border-p2w-line flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-p2w-purple" />
                    <h3 className="font-sora font-bold text-white">Imagen del hero (Home)</h3>
                </div>
                <div className="p-5 grid lg:grid-cols-2 gap-6">
                    {/* Preview */}
                    <div className="rounded-xl border border-p2w-line bg-p2w-bg/40 overflow-hidden">
                        <div className="px-4 py-2 border-b border-p2w-line flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-wider text-p2w-gray font-semibold">Preview actual</span>
                            <a
                                href={getFullUrl(config.image_url)}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[10px] text-p2w-gray hover:text-p2w-green inline-flex items-center gap-1"
                            >
                                Ver original <ExternalLink className="w-3 h-3" />
                            </a>
                        </div>
                        {config.image_url ? (
                            <img
                                src={getFullUrl(config.image_url)}
                                alt="Hero preview"
                                className="w-full aspect-video object-cover bg-p2w-bg"
                                data-testid="hero-preview-image"
                            />
                        ) : (
                            <div className="aspect-video flex items-center justify-center text-p2w-gray text-sm">Sin imagen</div>
                        )}
                        <div className="px-4 py-2 text-[10px] text-p2w-gray font-mono break-all">{config.image_url}</div>
                    </div>

                    {/* Controls */}
                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-p2w-gray">
                            Sube una imagen para el bloque superior del Home. Recomendado: <b className="text-white">1600×900</b>,
                            PNG/JPG/WEBP, máx <b className="text-white">8 MB</b>. Se ajusta automáticamente al diseño dark del fondo.
                        </p>
                        <label className="cursor-pointer">
                            <input
                                ref={inputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleUpload}
                                disabled={uploading}
                                className="hidden"
                                data-testid="hero-image-input"
                            />
                            <Button
                                asChild
                                disabled={uploading}
                                className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold py-6"
                            >
                                <span data-testid="hero-upload-btn"><ImagePlus className="w-4 h-4 mr-2" />{uploading ? "Subiendo…" : "Subir nueva imagen"}</span>
                            </Button>
                        </label>
                        <Button
                            onClick={resetImage}
                            variant="outline"
                            className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line"
                            data-testid="hero-reset-btn"
                        >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Restaurar imagen original
                        </Button>
                        <div className="mt-2 text-[11px] text-p2w-gray bg-p2w-elevated rounded-lg border border-p2w-line p-3 leading-relaxed">
                            💡 La imagen se sirve desde el backend, así que los cambios son inmediatos sin necesidad de redesplegar.
                        </div>
                    </div>
                </div>
            </div>

            {/* FEATURED MARKET SECTION */}
            <div className="rounded-2xl border border-p2w-line bg-p2w-surface overflow-hidden" data-testid="hero-market-section">
                <div className="px-5 py-3.5 border-b border-p2w-line flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-400" />
                    <h3 className="font-sora font-bold text-white">Mercado destacado del Home</h3>
                </div>
                <div className="p-5 space-y-4">
                    {config.market ? (
                        <div className="rounded-xl border border-p2w-green/40 bg-p2w-green/5 p-4 flex items-start justify-between gap-3" data-testid="current-hero-market">
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider text-p2w-green font-semibold">Actualmente destacado</div>
                                <div className="mt-1 font-sora font-bold text-white text-base break-words">{config.market.title}</div>
                                <div className="mt-1 text-xs text-p2w-gray">
                                    Pozo: <span className="text-p2w-green number-tabular">{config.market.total_pool} pts</span>
                                    {" · "}Status: <span className="text-white">{config.market.status}</span>
                                </div>
                            </div>
                            <Button
                                onClick={() => setMarket(null)}
                                disabled={savingMarket}
                                variant="outline"
                                size="sm"
                                className="border-rose-400/40 bg-rose-400/5 text-rose-400 hover:bg-rose-400/10 shrink-0"
                                data-testid="clear-hero-market"
                            >
                                <X className="w-3.5 h-3.5 mr-1.5" /> Quitar
                            </Button>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-p2w-line bg-p2w-elevated p-4 text-sm text-p2w-gray">
                            No hay un mercado destacado configurado. El Home mostrará el primer mercado destacado de la API automáticamente.
                        </div>
                    )}

                    <div>
                        <div className="relative">
                            <Search className="w-4 h-4 text-p2w-gray absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <Input
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); doSearch(e.target.value); }}
                                placeholder="Buscar mercado por nombre…"
                                className="pl-10 bg-p2w-elevated border-p2w-line text-white"
                                data-testid="hero-market-search"
                            />
                        </div>
                        {searching && <div className="mt-2 text-xs text-p2w-gray">Buscando…</div>}
                        {results.length > 0 && (
                            <div className="mt-3 rounded-xl border border-p2w-line bg-p2w-bg/40 max-h-72 overflow-y-auto">
                                {results.map((m) => (
                                    <button
                                        key={m.id}
                                        onClick={() => setMarket(m.id)}
                                        disabled={savingMarket}
                                        className="w-full text-left px-4 py-3 border-b border-p2w-line/40 last:border-0 hover:bg-p2w-elevated transition flex items-center justify-between gap-3"
                                        data-testid={`hero-market-option-${m.id.slice(0, 6)}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="font-medium text-white truncate">{m.title}</div>
                                            <div className="text-[11px] text-p2w-gray number-tabular">
                                                {m.status} · pozo {m.total_pool || 0} pts
                                                {m.is_live && <span className="ml-2 text-p2w-magenta">● LIVE</span>}
                                            </div>
                                        </div>
                                        <Star className="w-3.5 h-3.5 text-amber-400 opacity-0 group-hover:opacity-100" />
                                    </button>
                                ))}
                            </div>
                        )}
                        {search && !searching && results.length === 0 && (
                            <div className="mt-2 text-xs text-p2w-gray">Sin resultados</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function getFullUrl(path) {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    // For static files served by the backend through /api/static/
    if (path.startsWith("/api/")) {
        const base = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
        return `${base}${path}`;
    }
    // For public folder paths
    return path;
}
